'use client';

import React, { useRef, useState, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link, usePathname } from '@/navigation';
import type { LanguageCodeEnum } from '@/shared/config/common/constants/lang';
import { getUserCity } from '@/shared/lib/city/getUserCity';
import { classNames } from '@/shared/lib/classnames/classnames';
import { getFormattedDate } from '@/shared/lib/date/getFormattedDate';
import { Button } from '@/shared/ui/button';
import { Stars } from '@/shared/ui/stars';
import { getEmojiForRating } from '../utils/reviews';
import { ReplyContent } from './ReplyContent';
import type { IReview } from '@/entities/review/model/types';
interface Props extends IReview {
  avatar: string;
  alias: string;
}
export const ReviewItem: React.FC<Props> = ({
  authorName,
  rating,
  createdAt,
  tags = [],
  text,
  reply,
  alias
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const pathname = usePathname();
  const toggleExpanded = () => {
    setIsExpanded(prevState => !prevState);
  };
  const t = useTranslations('doctor.reviews');
  const locale = useLocale();
  useEffect(() => {
    if (textRef.current) {
      setIsClamped(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, [text]);
  return <div className="mt-4 rounded-2xl border border-gray-200 p-4" data-sentry-component="ReviewItem" data-sentry-source-file="ReviewItem.tsx">
      <div className="flex items-center">
        <div className="mr-4 flex h-[45px] w-[50px] items-center justify-center rounded-full bg-gray-200">
          <span className="text-[16px]">{getEmojiForRating(rating)}</span>
        </div>
        <div className="flex w-full flex-col">
          <div className="flex items-center justify-between">
            <p translate="no" className="font-semibold">
              {authorName}
            </p>
            <span className="ml-2 text-xs text-gray-500">
              {getFormattedDate(createdAt, {
              locale: locale as LanguageCodeEnum,
              format: 'DD MMMM YYYY',
              capitalizeFirst: true
            })}
            </span>
          </div>
          <div className="flex items-center">
            <Stars rating={rating} starColor="text-statusInfo" data-sentry-element="Stars" data-sentry-source-file="ReviewItem.tsx" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap pl-14">
        {tags?.map((tag, index) => <span key={`tags-${index}`} className="mb-2 mr-2 rounded-xl bg-bgSecondary px-2 py-1 text-sm text-gray-800">
            {tag.name}
          </span>)}
      </div>

      <div className="mt-2 pl-14 text-sm">
        <p ref={textRef} className={classNames('', {
        'line-clamp-3': !isExpanded
      })}>
          {text}
        </p>
        {isClamped && <div className="mt-1">
            <Button variant="text" onClick={toggleExpanded} textWeight="normal" className="py-0">
              {isExpanded ? `${t('hide')}` : `${t('expand')}`}
            </Button>
          </div>}
      </div>

      {reply && <div className="ml-14 mt-4 rounded">
          <p className="mb-4 text-xs text-gray-500">{`${t('reply.answer')}`}</p>
          {Boolean(reply.alias) && reply.alias !== alias ? <Link href={`/${getUserCity(pathname)}/doctor/${reply.alias}`}>
              <ReplyContent authorName={reply.authorName} avatar={reply.avatar} text={reply.text} createdAt={createdAt} />
            </Link> : <ReplyContent authorName={reply.authorName} avatar={reply.avatar} text={reply.text} createdAt={createdAt} />}
        </div>}
    </div>;
};